/**
 * Marked-time comparison ("Add time to compare") — PLAN.md "Marked-time comparison & AI narrowing".
 *
 * While drilling through the heatmap the user can MARK any exact candidate second as "likely"
 * ("Add time to compare" on the Level-1 detail panel). The marked times collect on
 * `profile.marked_times` and a dedicated comparison page lays them out side by side — the same
 * bar-strip UI as the heatmap, but split into the user's OWN shortlist instead of sequential
 * hours — with each time's full chart profile (D1/D9/D10/D60 lagnas, dasha timeline, planet
 * placements) and a facet-by-facet difference table.
 *
 * Everything in this module is DETERMINISTIC: the scores are the same `score_candidate_record`
 * engine the heatmap uses (D60 engaged — these are exact seconds, so the finest divisional layer
 * applies), and the chart facts are plain ephemeris arithmetic. The AI narrowing chat
 * (`compare_chat.ts`) receives this module's output as CONTEXT and asks the user discriminating
 * questions — it never ranks and never invents a chart fact (PLAN.md: AI never ranks).
 */

import { calculate_chart, get_nakshatra, local_to_jd } from "../astro/chart";
import { get_config } from "../astro/config";
import { DASHA_DAYS, DASHA_SEQUENCE, SIGN_NAMES, SIGN_RULERS } from "../astro/constants";
import { get_divisional_position } from "../astro/transit_data";
import { jd_to_ymd } from "./eras";
import { GRAHA_LABELS, whole_sign_house } from "./chart_overview";
import {
  birth_data_for_scoring,
  candidate_birth_date,
  candidate_key_seconds,
  candidate_timezone_offset,
  event_payload_to_life_event,
  make_candidate_key,
  parse_candidate_key,
  score_candidate_record,
  time_label_to_tuple,
  type CandidateTuple,
} from "./scoring";
import { combined_trait_answers } from "./traits";
import { init_dasha_jd } from "../astro/dasha";
import { formatISODate, parseISODate, plainDateTime, type PlainDate } from "./datetime";

/** One user-marked "likely" birth time (seconds precision, pinned to a calendar day). */
export interface MarkedTime {
  /** "HH:MM:SS" clock time (seconds always present). */
  time: string;
  /** ISO calendar day the time refers to (resolved at mark time — overnight/multi-day safe). */
  date: string;
  /** Rising sign at this moment, captured when marked (display convenience). */
  sign?: string | null;
  /** ISO timestamp of when the user marked it. */
  added_at?: string;
  [key: string]: any;
}

/** Sanity cap — a comparison of more than this stops being a shortlist. */
export const MAX_MARKED_TIMES = 24;

/** Stable identity of a marked time: its date-qualified candidate key. */
export function marked_time_key(entry: Pick<MarkedTime, "time" | "date">): string {
  return `${entry.date}T${entry.time}`;
}

/** The saved marked-time list, cleaned (well-formed entries only) and in chronological order. */
export function normalize_marked_times(profile: Record<string, any>): MarkedTime[] {
  const raw: any[] = Array.isArray(profile?.marked_times) ? profile.marked_times : [];
  const seen = new Set<string>();
  const out: MarkedTime[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const time = String(item.time ?? "");
    const date = String(item.date ?? "");
    try {
      time_label_to_tuple(time);
      parseISODate(date);
    } catch {
      continue;
    }
    const key = marked_time_key({ time, date });
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...item, time, date });
  }
  out.sort((a, b) => candidate_key_seconds(marked_time_key(a)) - candidate_key_seconds(marked_time_key(b)));
  return out;
}

/**
 * Resolve the calendar day a candidate TIME LABEL refers to, given the profile's CURRENT search
 * window — the same overnight/multi-day rule the scoring engine uses (`candidate_birth_date`),
 * so a post-midnight candidate inside a 21:00→01:00 window lands on the day after the birth
 * date, and a date-qualified multi-day key keeps its own explicit day.
 */
export function resolve_marked_date(profile: Record<string, any>, time_label: string): string {
  const parsed = parse_candidate_key(time_label);
  if (parsed.date) return formatISODate(parsed.date);
  const birth = profile?.birth ?? {};
  let base: PlainDate;
  try {
    base = parseISODate(birth.date);
  } catch {
    return String(birth.date ?? "");
  }
  const birth_info = { year: base.year, month: base.month, date: base.day };
  const day = candidate_birth_date(birth_info, parsed.tuple, profile?.search ?? null);
  return formatISODate(day);
}

/**
 * Add a candidate time to the comparison shortlist (in place, deduped, capped). `time_label`
 * may be plain "HH:MM:SS" or a date-qualified multi-day key. Returns the entry's key, or null
 * if it could not be added (malformed / over the cap).
 */
export function add_marked_time(
  profile: Record<string, any>,
  time_label: string,
  sign: string | null = null,
  added_at: string | null = null,
): string | null {
  const parsed = parse_candidate_key(time_label);
  const time = make_candidate_key(parsed.tuple, null);
  const date = resolve_marked_date(profile, time_label);
  try {
    parseISODate(date);
  } catch {
    return null;
  }
  const entry: MarkedTime = { time, date, sign: sign ?? null };
  if (added_at) entry.added_at = added_at;
  const key = marked_time_key(entry);
  const current = normalize_marked_times(profile);
  if (current.some((e) => marked_time_key(e) === key)) return key;
  if (current.length >= MAX_MARKED_TIMES) return null;
  profile.marked_times = [...current, entry];
  return key;
}

/** Remove a marked time (and any stale elimination/assessment/rank/final referring to it) in place. */
export function remove_marked_time(profile: Record<string, any>, key: string): void {
  profile.marked_times = normalize_marked_times(profile).filter((e) => marked_time_key(e) !== key);
  if (Array.isArray(profile.compare_eliminated)) {
    profile.compare_eliminated = profile.compare_eliminated.filter((k: string) => k !== key);
  }
  if (profile.compare_assessments && typeof profile.compare_assessments === "object") {
    delete profile.compare_assessments[key];
  }
  if (profile.compare_ranks && typeof profile.compare_ranks === "object") {
    delete profile.compare_ranks[key];
  }
  if (Array.isArray(profile.compare_chat_keys)) {
    // Keep the conversation's key bookkeeping in step: a pure removal must never read as
    // "new keys appeared" to compare_conversation_stale.
    profile.compare_chat_keys = profile.compare_chat_keys.filter((k: string) => k !== key);
  }
  if (profile.compare_final_key === key) profile.compare_final_key = null;
}

// ── Conversation ↔ shortlist reconcile (owner spec 2026-07-05) ──────────────────────────────────

/** True when a narrowing conversation exists for this profile (server thread or a local log). */
export function has_compare_conversation(profile: Record<string, any>): boolean {
  return (
    Boolean(profile?.compare_conversation_id) ||
    (Array.isArray(profile?.compare_chat) && profile.compare_chat.length > 0)
  );
}

/**
 * True when the running conversation no longer covers the shortlist: a NEW marked time joined
 * AFTER the conversation started. `compare_chat_keys` records the keys the conversation has seen
 * (stamped on every successful turn); every question, assessment and running rank was built over
 * that smaller set, so a new piece invalidates the whole narrowing. (A conversation with no key
 * bookkeeping — legacy — is treated as stale once, resetting it onto the current shortlist.)
 * Pure REMOVALS never trigger this: the thread continues and the UI tells the assistant instead.
 */
export function compare_conversation_stale(profile: Record<string, any>): boolean {
  if (!has_compare_conversation(profile)) return false;
  const known = new Set<string>(Array.isArray(profile?.compare_chat_keys) ? profile.compare_chat_keys : []);
  return normalize_marked_times(profile).some((e) => !known.has(marked_time_key(e)));
}

/**
 * Wipe the narrowing conversation and the AI's scores over it (chat log, server conversation id,
 * assessments, running ranks, key bookkeeping) so a fresh conversation auto-opens over the CURRENT
 * shortlist. The user's committed FINAL pick is kept — it is their decision, not an AI score.
 */
export function reset_compare_conversation(profile: Record<string, any>): void {
  profile.compare_chat = [];
  profile.compare_conversation_id = null;
  profile.compare_assessments = {};
  profile.compare_ranks = {};
  delete profile.compare_chat_keys;
}

// ── The FINAL pick (the user's committed birth time) ────────────────────────────────────────────

/**
 * The marked-time key the user has committed to as THE birth time, or null if none. This is the
 * user's decision — a settled answer, distinct from the engine's ranking and the AI's advice. It
 * is what the `finals` DynamoDB record records.
 */
export function final_marked_key(profile: Record<string, any>): string | null {
  const key = profile?.compare_final_key;
  if (typeof key !== "string" || !key) return null;
  // Only honour a key still on the shortlist.
  return normalize_marked_times(profile).some((e) => marked_time_key(e) === key) ? key : null;
}

/** Set (or clear, with null) the user's committed final birth time, in place. Toggles off if re-picked. */
export function set_final_marked_key(profile: Record<string, any>, key: string | null): string | null {
  if (key === null) {
    profile.compare_final_key = null;
    return null;
  }
  const onList = normalize_marked_times(profile).some((e) => marked_time_key(e) === key);
  profile.compare_final_key = onList && profile.compare_final_key !== key ? key : null;
  return profile.compare_final_key ?? null;
}

/**
 * The DynamoDB `finals` decision record for a person: their marked-times shortlist, the user's own
 * promote/demote ranking, eliminations, the display-only AI assessments, the narrowing chat, and
 * (when chosen) the committed FINAL birth time — a settled, independently-queryable snapshot of the
 * comparison outcome. Returns null when there is no shortlist to record (nothing to persist).
 */
export function build_finals_record(
  profile: Record<string, any>,
  updated_at: string | null = null,
): Record<string, any> | null {
  const marked = normalize_marked_times(profile);
  if (!marked.length) return null;
  const finalKey = final_marked_key(profile);
  const finalEntry = finalKey ? marked.find((e) => marked_time_key(e) === finalKey) ?? null : null;
  const birth = profile?.birth ?? {};
  return {
    schema_version: 1,
    slug: profile?.slug ?? null,
    first_name: profile?.first_name ?? null,
    birth: {
      date: birth.date ?? null,
      place: birth.place ?? birth.location ?? null,
      lat: birth.lat ?? null,
      lon: birth.lon ?? null,
      timezone_offset: birth.timezone_offset ?? null,
    },
    final_choice: finalEntry
      ? {
          key: finalKey,
          time: finalEntry.time,
          date: finalEntry.date,
          sign: finalEntry.sign ?? null,
        }
      : null,
    marked_times: marked,
    eliminated: Array.isArray(profile?.compare_eliminated) ? profile.compare_eliminated : [],
    ranks:
      profile?.compare_ranks && typeof profile.compare_ranks === "object" ? profile.compare_ranks : {},
    assessments:
      profile?.compare_assessments && typeof profile.compare_assessments === "object"
        ? profile.compare_assessments
        : {},
    chat: Array.isArray(profile?.compare_chat) ? profile.compare_chat : [],
    conversation_id: profile?.compare_conversation_id ?? null,
    updated_at,
  };
}

// ── User ranking (promote / demote — a METRIC, not a checkbox) ────────────────────────────────

/** The rank level is clamped to ±RANK_LIMIT — enough steps to order a 24-item shortlist. */
export const RANK_LIMIT = 5;

/** The user's promote/demote standing for a marked time (integer, default 0 = unranked). */
export function marked_rank(profile: Record<string, any>, key: string): number {
  const ranks = profile?.compare_ranks;
  const v = ranks && typeof ranks === "object" ? Number(ranks[key]) : 0;
  return Number.isFinite(v) ? Math.max(-RANK_LIMIT, Math.min(RANK_LIMIT, Math.trunc(v))) : 0;
}

/**
 * Promote (+1) / demote (−1) a marked time in place, clamped to ±RANK_LIMIT. This is the USER's
 * preference metric — it lives beside the engine scores (its own bar metric + standings order)
 * and is TOLD to the AI, but it never enters the deterministic engine's ranking. Returns the new
 * level. A pure preference edit: never resets loop_count, never re-scans.
 */
export function adjust_marked_rank(profile: Record<string, any>, key: string, delta: number): number {
  const next = Math.max(-RANK_LIMIT, Math.min(RANK_LIMIT, marked_rank(profile, key) + Math.trunc(delta)));
  const ranks = (profile.compare_ranks ??= {});
  if (next === 0) delete ranks[key];
  else ranks[key] = next;
  return next;
}

// ── Chart facts per marked time ───────────────────────────────────────────────────────────────

const _FACT_GRAHAS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"] as const;

/**
 * Deterministic chart profile for one exact second: the structural facts that actually DIFFER
 * between nearby candidate times and are therefore worth asking the user about — the D1 lagna
 * (sign + degree), the fast divisional lagnas (D9 ~13 min, D10 ~12 min, D60 ~2 min), the Moon's
 * nakshatra/pada (sets the dasha balance), the lagna lord and his house, planet→house
 * placements, and the mahadasha timeline with its calendar transition years.
 */
export function chart_facts_for_time(
  profile: Record<string, any>,
  entry: MarkedTime,
): Record<string, any> | null {
  const birth = profile?.birth ?? {};
  if (birth.lat == null || birth.lon == null || birth.timezone_offset == null) return null;
  let day: PlainDate;
  let tuple: CandidateTuple;
  try {
    day = parseISODate(entry.date);
    tuple = time_label_to_tuple(entry.time);
  } catch {
    return null;
  }
  const lat = Number(birth.lat);
  const lon = Number(birth.lon);
  const birth_info = {
    ...birth,
    year: day.year,
    month: day.month,
    date: day.day,
  };
  const tz = candidate_timezone_offset(tuple, birth_info, Number(birth.timezone_offset), null, day);
  const dt = plainDateTime(day.year, day.month, day.day, tuple[0], tuple[1], tuple[2]);
  const chart = calculate_chart(dt, lat, lon, tz, get_config());

  const asc = chart.planets["ascendant"];
  const moon = chart.planets["moon"];
  const asc_sign = Math.trunc(asc.sign);
  const asc_deg_in_sign = asc.longitude - asc_sign * 30;
  const [, moon_nak_name, moon_pada] = get_nakshatra(moon.longitude);

  const varga_lagna = (division: number): string => {
    const [sign] = get_divisional_position(asc.longitude, division);
    return SIGN_NAMES[sign];
  };

  const placements: Record<string, { sign_name: string; house: number }> = {};
  for (const name of _FACT_GRAHAS) {
    const planet = chart.planets[name];
    if (!planet) continue;
    const sign = Math.trunc(planet.sign);
    placements[GRAHA_LABELS[name] ?? name] = {
      sign_name: SIGN_NAMES[sign],
      house: whole_sign_house(sign, asc_sign),
    };
  }
  const lagna_lord = SIGN_RULERS[asc_sign];
  const lagna_lord_label = GRAHA_LABELS[lagna_lord] ?? lagna_lord;

  // Mahadasha timeline from birth to ~present (the transition YEARS are the sharpest lived
  // difference between nearby birth times — "did life visibly change around 2009 or 2013?").
  const birth_jd = local_to_jd(dt, tz);
  const dinit = init_dasha_jd(chart, birth_jd);
  const order = DASHA_SEQUENCE.map(([l]) => l);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const horizon_jd = birth_jd + 95 * 365.25;
  const timeline: Array<{ lord: string; start_year: number; end_year: number }> = [];
  let idx = dinit["start_index"];
  let start = birth_jd;
  let end = dinit["first_dasha_end_jd"];
  let guard = 0;
  while (start < horizon_jd && guard < 12) {
    timeline.push({
      lord: cap(order[idx]),
      start_year: jd_to_ymd(start).year,
      end_year: jd_to_ymd(end).year,
    });
    idx = (idx + 1) % 9;
    start = end;
    end = start + DASHA_DAYS[order[idx]];
    guard += 1;
  }

  return {
    key: marked_time_key(entry),
    d1_lagna: SIGN_NAMES[asc_sign],
    d1_lagna_degree: Math.round(asc_deg_in_sign * 100) / 100,
    lagna_lord: lagna_lord_label,
    lagna_lord_house: placements[lagna_lord_label]?.house ?? null,
    d9_lagna: varga_lagna(9),
    d10_lagna: varga_lagna(10),
    d60_lagna: varga_lagna(60),
    moon_sign: SIGN_NAMES[Math.trunc(moon.sign)],
    moon_nakshatra: moon_nak_name,
    moon_pada: moon_pada,
    first_mahadasha: timeline.length ? timeline[0].lord : null,
    dasha_timeline: timeline,
    placements,
  };
}

// ── The comparison itself ─────────────────────────────────────────────────────────────────────

/** The facet catalogue rendered as difference-table rows (and handed to the AI as evidence). */
const FACET_DEFS: Array<{ key: string; label: string; value: (f: Record<string, any>) => string }> = [
  { key: "d1_lagna", label: "Rising sign (D1)", value: (f) => `${f.d1_lagna} ${f.d1_lagna_degree}°` },
  {
    key: "lagna_lord",
    label: "Lagna lord & house",
    value: (f) => `${f.lagna_lord}${f.lagna_lord_house ? ` in house ${f.lagna_lord_house}` : ""}`,
  },
  { key: "d9_lagna", label: "Navamsa lagna (D9 — marriage/dharma)", value: (f) => String(f.d9_lagna) },
  { key: "d10_lagna", label: "Dashamsa lagna (D10 — career)", value: (f) => String(f.d10_lagna) },
  { key: "d60_lagna", label: "Shashtiamsa lagna (D60 — finest karmic)", value: (f) => String(f.d60_lagna) },
  { key: "moon_nakshatra", label: "Moon nakshatra · pada", value: (f) => `${f.moon_nakshatra} · pada ${f.moon_pada}` },
  {
    key: "dasha_transitions",
    label: "Mahadasha transitions",
    value: (f) =>
      (f.dasha_timeline ?? [])
        .slice(0, 5)
        .map((p: any) => `${p.lord} ${p.start_year}–${p.end_year}`)
        .join(" · "),
  },
];

/**
 * Score + profile every marked time. Pure and synchronous (runs in the engine worker as the
 * `compare_marked_times` op). Returns `{available:false, reason}` when it can't run, else:
 *
 *   {
 *     available: true,
 *     event_count,
 *     times:  [{ key, time, date, sign, facts, record }],   // chronological
 *     facets: [{ key, label, values: {timeKey: string}, differs }],
 *   }
 *
 * `record` is the FULL `score_candidate_record` output (the same record shape the Level-1
 * heatmap consumes: event_fit_score / accuracy_fit / deterministic_score / kp_details /
 * special_points / appearance_note …). Each time is scored with a per-entry NARROW synthetic
 * window on its OWN calendar day so the fine-divisional (D60) layer engages — marked times are
 * exact seconds, exactly the case the classical rule reserves D60 for.
 */
export function compare_marked_times(profile: Record<string, any>): Record<string, any> {
  const entries = normalize_marked_times(profile);
  if (!entries.length) {
    return { available: false, reason: "No times marked for comparison yet." };
  }
  const birth = profile?.birth ?? {};
  if (birth.date == null || birth.lat == null || birth.lon == null || birth.timezone_offset == null) {
    return { available: false, reason: "Birth details (date, place, offset) are incomplete." };
  }
  const slug = String(profile.slug ?? "user");
  const birth_data = birth_data_for_scoring(profile);
  const timezone_offset = Number(birth.timezone_offset);
  const raw_events: Array<Record<string, any>> = (profile.events ?? []).filter(
    (e: any) => typeof e === "object" && e !== null && e.start_date,
  );
  const life_events = [];
  for (const event of raw_events) {
    try {
      life_events.push(event_payload_to_life_event(event));
    } catch {
      continue;
    }
  }
  const trait_answers = combined_trait_answers(profile);
  const life_period_answers = profile.life_periods ?? {};

  const times: Array<Record<string, any>> = [];
  for (const entry of entries) {
    const key = marked_time_key(entry);
    const tuple = time_label_to_tuple(entry.time);
    const day = parseISODate(entry.date);
    // A tight same-day window: pins the candidate to ITS calendar day and keeps the span far
    // under FINE_DIVISIONAL_MAX_SPAN_SECONDS so score_candidate_record engages D60.
    const search = {
      start: entry.time,
      end: entry.time,
      start_date: entry.date,
      end_date: entry.date,
    };
    const record = score_candidate_record(
      slug,
      tuple,
      birth_data,
      life_events,
      raw_events,
      timezone_offset,
      {},
      "marked_time_compare",
      null,
      search,
      trait_answers,
      life_period_answers,
      null, // AI appearance likelihood NEVER enters ranking (PLAN.md)
      day,
    );
    times.push({
      key,
      time: entry.time,
      date: entry.date,
      sign: entry.sign ?? record["ascendant_sign"] ?? null,
      added_at: entry.added_at ?? null,
      facts: chart_facts_for_time(profile, entry),
      record,
    });
  }

  const facets = FACET_DEFS.map((def) => {
    const values: Record<string, string> = {};
    for (const t of times) {
      values[t.key] = t.facts ? def.value(t.facts) : "—";
    }
    const distinct = new Set(Object.values(values));
    return { key: def.key, label: def.label, values, differs: distinct.size > 1 };
  });

  return { available: true, event_count: raw_events.length, times, facets };
}

// ── AI context (consumed by compare_chat.ts — deterministic input, never ranking output) ──────

/**
 * Compact, deterministic JSON context handed to the narrowing chat every turn: each marked
 * time's chart facts + engine scores, which facets actually differ, what the user has already
 * eliminated, and the running AI assessments. Built from `compare_marked_times` output so the
 * model can only reason over REAL chart differences.
 */
export function compare_context_for_ai(
  comparison: Record<string, any>,
  eliminated: string[] = [],
  assessments: Record<string, { verdict: string; reason?: string }> = {},
  user_ranks: Record<string, number> = {},
): Record<string, any> {
  const times = (comparison?.times ?? []).map((t: any) => ({
    key: t.key,
    time: t.time,
    date: t.date,
    rising_sign: t.sign,
    // The shortlist's RUNNING rank (−RANK_LIMIT..+RANK_LIMIT, 0 = unranked). The assistant drives
    // this now: its rank_suggestions are auto-applied one step per turn, so it sees the current
    // level to decide the next promote/demote/keep from the user's latest answer.
    running_rank: Number.isFinite(Number(user_ranks[t.key])) ? Math.trunc(Number(user_ranks[t.key])) : 0,
    engine_scores: {
      events_fit: t.record?.event_fit_score ?? null,
      accuracy_fit: t.record?.accuracy_fit ?? null,
      overall: t.record?.deterministic_score ?? null,
    },
    chart: t.facts
      ? {
          d1_lagna: `${t.facts.d1_lagna} ${t.facts.d1_lagna_degree}°`,
          lagna_lord: t.facts.lagna_lord,
          lagna_lord_house: t.facts.lagna_lord_house,
          d9_lagna: t.facts.d9_lagna,
          d10_lagna: t.facts.d10_lagna,
          d60_lagna: t.facts.d60_lagna,
          moon_nakshatra: `${t.facts.moon_nakshatra} pada ${t.facts.moon_pada}`,
          dasha_timeline: (t.facts.dasha_timeline ?? []).slice(0, 6),
          placements: t.facts.placements,
        }
      : null,
    prior_assessment: assessments[t.key] ?? null,
  }));
  const differing = (comparison?.facets ?? []).filter((f: any) => f.differs).map((f: any) => f.label);
  return {
    marked_times: times,
    facets_that_differ: differing,
    note:
      "engine_scores are the deterministic Jyotish engine's verdicts (events rank the time) and " +
      "stay separate. running_rank is the shortlist rank YOU drive: your rank_suggestions are " +
      "auto-applied one step per turn. Ask one grounded, differing-facet question per turn (with " +
      "options), move on to a DIFFERENT facet once one is settled, promote/demote each affected " +
      "time from the user's answer, and say plainly which time the combined evidence favours. " +
      "Everything the user reads must be plain everyday language — never astrological terminology.",
  };
}
